/*
 * Renders the standards associated with a content item.
 * Standard descriptions appear on hover.
 */
import {css, StyleSheet} from "aphrodite";
import * as React from "react";

import Link from "@khanacademy/wonder-blocks-link";
import {color} from "@khanacademy/wonder-blocks-tokens";
import Tooltip from "@khanacademy/wonder-blocks-tooltip";
import {Body} from "@khanacademy/wonder-blocks-typography";
import {markMinorConversions} from "@khan/analytics-learning-time";
import {useFastlyQuery} from "@khan/data-gql";
import {useRegionOverride} from "@khan/data-region-override";
import {useUserAttributes} from "@khan/shared-core";

import {groupBy, keepMostSpecificCommonCoreMathStandards} from "./helpers.ts";
import {STANDARD_DESCRIPTION_QUERY} from "./standard-description-query.ts";
import type {Standard} from "./types.ts";

interface StandardCodeDescriptionTooltipWrapperProps {
    wrappedCode: React.ReactElement | null;
    standard: Standard;
    small: boolean;
}

const StandardCodeDescriptionTooltipWrapper = (
    props: StandardCodeDescriptionTooltipWrapperProps,
): React.ReactElement | null => {
    const {standard, small, wrappedCode} = props;
    const regionResult = useRegionOverride();
    const options =
        regionResult.status !== "success" || standard.descriptionHtml
            ? {skip: true}
            : {
                  variables: {
                      region: regionResult.regionCode,
                      setId: standard.set.id,
                      standardId: standard.code,
                  },
              };
    const standardResult = useFastlyQuery(STANDARD_DESCRIPTION_QUERY, options);

    if (standard.descriptionHtml) {
        // TODO(djf): once we stop passing descriptionHtml from the
        // coach dashboard, we can get rid of this branch and always use
        // the query below instead.
        return (
            <Tooltip
                content={
                    <Body style={[styles.description, small && styles.small]}>
                        {standard.descriptionHtml}
                    </Body>
                }
                placement="bottom"
            >
                <span className={css(styles.tooltipChild)}>{wrappedCode}</span>
            </Tooltip>
        );
    }

    if (
        regionResult.status !== "success" ||
        standardResult.status !== "success" ||
        !standardResult.data ||
        !standardResult.data.publishedStandard ||
        !standardResult.data.publishedStandard.description
    ) {
        return wrappedCode;
    }

    return (
        <Tooltip
            content={
                <Body style={[styles.description, small && styles.small]}>
                    {standardResult.data.publishedStandard.description}
                </Body>
            }
            placement="bottom"
        >
            <span className={css(styles.tooltipChild)}>{wrappedCode}</span>
        </Tooltip>
    );
};

interface StandardCodeWithDescriptionProps {
    standard: Standard;
    small: boolean;
    linkColor?: typeof color.blue | typeof color.white;
    showDescription?: boolean;
    handleStandardClick: (standard: Standard) => void;
}

const StandardCodeWithDescription = (
    props: StandardCodeWithDescriptionProps,
): React.ReactElement | null => {
    const {standard, small, linkColor, showDescription, handleStandardClick} =
        props;

    // Prevent codes with hyphens from breaking across lines
    const code = standard.code.replace(/-/g, "\u2011");

    // TODO(djf): once the coach dashboard stops passing the link, we
    // can change this code to always just call the function.
    const url = standard.link ?? "";
    const wrappedCode = standard.set.isDiscoverable ? (
        <Link
            style={[
                linkColor !== color.white && styles.linkColorBlue,
                linkColor === color.white && styles.linkColorWhite,
                styles.link,
                small && styles.small,
            ]}
            href={url}
            target="_blank"
            safeWithNav={() =>
                markMinorConversions([
                    {
                        id: "standard_click",
                        extra: {
                            code: standard.code,
                        },
                    },
                ])
            }
            onClick={() => {
                handleStandardClick(standard);
            }}
        >
            {code}
        </Link>
    ) : (
        <span className={css(small && styles.small)}>{code}</span>
    );

    if (showDescription) {
        return (
            <StandardCodeDescriptionTooltipWrapper
                small={small}
                standard={standard}
                wrappedCode={wrappedCode}
            />
        );
    }
    return wrappedCode;
};

interface StandardsProps {
    standards: Array<Standard>;
    showSetPrefix?: boolean;
    small?: boolean;
    linkColor?: typeof color.blue | typeof color.white;
    showDescriptionsForLearners?: boolean;
    handleStandardClick: (standard: Standard) => void;
}

const Standards: React.FC<StandardsProps> = (props) => {
    const {
        standards,
        showSetPrefix = true,
        small = false,
        linkColor = color.blue,
        showDescriptionsForLearners = true,
        handleStandardClick,
    } = props;

    const userAttributes = useUserAttributes();

    const userIsCoachOrParent =
        userAttributes?.hasChildren || userAttributes?.hasCoachees;

    const isLearner = !userIsCoachOrParent;
    const showDescription = isLearner ? !!showDescriptionsForLearners : true;

    const setsOfStandards = groupBy(
        standards,
        (standard) => standard.set.prefix,
    );
    const setPrefixes = Object.keys(setsOfStandards);

    // The old hard-coded common core standards have the prefix "Common Core",
    // but the new published standards use "Common Core Math" instead. In either
    // case we want to prune long lists if we can. This is another special case
    // just for common core standards.
    for (const prefix of setPrefixes) {
        if (prefix.startsWith("Common Core")) {
            setsOfStandards[prefix] = keepMostSpecificCommonCoreMathStandards(
                setsOfStandards[prefix],
            );
        }
    }

    return (
        <div>
            {setPrefixes.map((prefix, setIndex) => (
                <Body
                    key={prefix}
                    style={[
                        styles.set,
                        small && styles.small,
                        linkColor === color.white && styles.linkColorWhite,
                    ]}
                >
                    {showSetPrefix && `${prefix}: `}
                    {setsOfStandards[prefix].map((standard, standardIndex) => (
                        <span key={standard.code}>
                            <StandardCodeWithDescription
                                standard={standard}
                                small={small ?? false}
                                linkColor={linkColor}
                                showDescription={showDescription}
                                handleStandardClick={handleStandardClick}
                            />
                            {standardIndex !==
                                setsOfStandards[prefix].length - 1 && ", "}
                        </span>
                    ))}
                    {setIndex !== setPrefixes.length - 1 && " "}
                </Body>
            ))}
        </div>
    );
};

const styles = StyleSheet.create({
    description: {
        display: "block",
        maxWidth: 360,
    },
    tooltipChild: {
        display: "inline-block",
    },
    linkColorBlue: {
        color: color.blue,
    },
    linkColorWhite: {
        color: color.white,
    },
    link: {
        textDecoration: "none",
        ":hover": {
            textDecoration: "underline",
        },
    },
    set: {
        display: "block",
    },
    small: {
        fontSize: 12,
        lineHeight: "14px",
    },
});

export default Standards;
